/**
 * Recursively computes only the keys that genuinely changed between
 * `original` and `updated`. Nested plain objects recurse (so a change
 * to identity.fonts.heading produces {fonts: {heading: ...}}, not the
 * entire fonts object); scalars and arrays are included wholesale if
 * they differ at all - no attempt to diff inside an array, since
 * merging array contents is ambiguous (same reasoning the backend's own
 * _deep_merge uses).
 *
 * This exists specifically so a save only ever sends what was actually
 * edited, not the caller's full, local copy of the object. Without
 * this, the backend's deep-merge has nothing to gain from being a merge
 * at all - if every field is present in the patch (even unchanged
 * ones), a stale, unrelated field the user never touched would still
 * silently overwrite a genuine, concurrent change made by someone else
 * in the meantime. Confirmed via a real, reported test case: two tabs
 * editing different sub-fields (fonts.heading vs fonts.body) of the
 * same brand's identity, without refreshing between edits.
 */
export function diffObject(original, updated) {
  if (
    typeof original !== "object" ||
    original === null ||
    Array.isArray(original) ||
    typeof updated !== "object" ||
    updated === null ||
    Array.isArray(updated)
  ) {
    return updated;
  }

  const patch = {};
  for (const key of Object.keys(updated)) {
    const origVal = original?.[key];
    const newVal = updated[key];
    if (
      typeof origVal === "object" &&
      origVal !== null &&
      !Array.isArray(origVal) &&
      typeof newVal === "object" &&
      newVal !== null &&
      !Array.isArray(newVal)
    ) {
      const nested = diffObject(origVal, newVal);
      if (Object.keys(nested).length > 0) patch[key] = nested;
    } else if (JSON.stringify(origVal) !== JSON.stringify(newVal)) {
      patch[key] = newVal;
    }
  }
  return patch;
}